import { FormEvent, useEffect, useState } from 'react';
import Image from 'next/image';

import { Ui } from '@silvertours/front-shared';
import { account, analytics, useSiteInfo } from '@silvertours/front-entities';

import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import {
  StyledForm,
  ImageWrapper,
  Subheadline,
  InputWrapper,
  PasswordLostWrapper,
  FormActionArea,
  MaxWidthWrapper,
  SignupWrapper,
  SignupHeadline,
  SubmitButton,
  SignupButton,
  BackToOptions,
  BackToOptionsLink,
} from './LoginForm.styles';
import AccountIllustration from './account_illustration.png';
import { LoginError } from './LoginError';
import { useLogin } from './LoginState';
import { SocialOptions } from './SocialOptions';

const LoginForm = () => {
  const [loading, setLoading] = useState(false);
  const {
    setUsername,
    setShowLogin,
    setIsLoggedIn,
    setError,
    isProviderStep,
    setEmailChosen,
  } = useLogin();
  const { featureToggles } = useSiteInfo();
  const { isSocialLoginActive } = featureToggles;

  const isLoggedIn = false;

  const { login } = account.useAccountRequest();

  useEffect(() => {
    setError(null);
  }, [setError]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const emailAddress = (
      event.currentTarget.elements.namedItem('emailAddress') as HTMLInputElement
    ).value;
    const password = (
      event.currentTarget.elements.namedItem('password') as HTMLInputElement
    ).value;

    setUsername(emailAddress);
    setLoading(true);
    const { loggedIn, error: errorMessage } = await login(
      emailAddress,
      password,
    );

    if (loggedIn) {
      setShowLogin(false);
      setIsLoggedIn(true);
    } else {
      setError(errorMessage || null);
      setIsLoggedIn(false);
    }
    setLoading(false);
  };

  const t = useTranslations('features.navigationLegacy.login');
  const router = useRouter();
  const actionPath = t('formAction', {
    redirectPath: encodeURIComponent(router.asPath),
  });

  // The provider buttons come first, and the email form sits behind them. The
  // control group must still see the form immediately.
  if (isProviderStep) {
    return <SocialOptions onEmailClick={() => setEmailChosen(true)} />;
  }

  return (
    <>
      <ImageWrapper>
        <Image src={AccountIllustration} alt="" unoptimized />
      </ImageWrapper>
      <Subheadline>{t('subTitle')}</Subheadline>

      <StyledForm
        method="post"
        action={actionPath}
        id="login-form"
        aria-label={t('formTitle')}
        onSubmit={handleSubmit}
      >
        <LoginError />

        <FormActionArea>
          <MaxWidthWrapper>
            <InputWrapper>
              <Ui.Input
                id="email"
                type="email"
                name="emailAddress"
                label={t('emailLabel')}
                autoComplete="section-login username"
                autoCorrect="off"
                autoCapitalize="none"
                required
              />
            </InputWrapper>
            <InputWrapper>
              <Ui.Input
                id="password"
                type="password"
                name="password"
                label={t('passwordLabel')}
                showButtonLabel={t('showPasswordLabel')}
                hideButtonLabel={t('hidePasswordLabel')}
                autoComplete="section-login current-password"
                required
              />
            </InputWrapper>

            <PasswordLostWrapper>
              <Ui.TextLink
                href={t('passwordLost.href')}
                text={t('passwordLost.text')}
                onClick={() =>
                  analytics.gtm.trackPasswordForgotten({ isLoggedIn })
                }
                data-testid="resetPasswordLink"
              />
            </PasswordLostWrapper>

            <SubmitButton
              type="submit"
              loading={loading}
              disabled={loading}
              onClick={() => {
                analytics.gtm.trackLogin();
              }}
              fullWidth
              data-testid="submitLogin"
            >
              {t('submitText')}
            </SubmitButton>
          </MaxWidthWrapper>
        </FormActionArea>
      </StyledForm>

      <SignupWrapper>
        <SignupHeadline>{t('noAccountText')}</SignupHeadline>
        <SignupButton
          type="button"
          variant="secondary"
          fullWidth
          onClick={() => {
            analytics.gtm.trackNewUser({ isLoggedIn });
            analytics.generic.trackLoginMenuRegisterClick({ isLoggedIn });
            window.location.assign(t('signup.href'));
          }}
          data-testid="signupButton"
        >
          {t('signup.text')}
        </SignupButton>
      </SignupWrapper>

      {isSocialLoginActive && (
        <BackToOptions>
          <BackToOptionsLink
            as="button"
            type="button"
            variant="text"
            text={t('social.back')}
            onClick={() => setEmailChosen(false)}
            data-testid="backToSocialOptions"
          />
        </BackToOptions>
      )}
    </>
  );
};

export { LoginForm };
